"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { sendOtp, verifyOtp, signInWithPassword, sendPasswordReset } from "@/app/auth/actions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Mail, Shield, Lock, MailCheck } from "lucide-react";

type AuthStep = "email" | "code" | "password" | "reset-sent";

export function AuthForm() {
  const [step, setStep] = useState<AuthStep>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/dashboard';

  const netErr = (msg: string) =>
    msg.toLowerCase().includes("failed to fetch") || msg.toLowerCase().includes("network") || msg.toLowerCase().includes("err_network")
      ? "Falha de conexão com o servidor. Verifique sua internet e tente novamente."
      : msg;

  // Passo 1: resolve o metodo do dominio e segue por codigo OU senha.
  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      let method: "otp" | "password" = "otp";
      try {
        const r = await fetch(`/api/auth/method?email=${encodeURIComponent(email)}`, { cache: "no-store" });
        if (r.ok) { const j = await r.json(); if (j?.method === "password") method = "password"; }
      } catch { /* cai no padrao: codigo */ }

      if (method === "password") { setStep("password"); return; }
      const { error } = await sendOtp(email);
      if (error) { setError(netErr(error) || "Ocorreu um erro. Tente novamente."); return; }
      setStep("code");
    } catch {
      setError("Ocorreu um erro inesperado. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { error } = await verifyOtp(email, code);
      if (error) { setError("Código inválido ou expirado. Tente novamente."); return; }
      router.push(redirectTo);
      router.refresh();
    } catch {
      setError("Ocorreu um erro inesperado. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { error } = await signInWithPassword(email, password);
      if (error) {
        const m = error.toLowerCase();
        if (m.includes("invalid login") || m.includes("credentials")) {
          setError("E-mail ou senha incorretos. Se é seu primeiro acesso, clique em “Definir / recuperar senha” abaixo.");
        } else {
          setError(netErr(error));
        }
        return;
      }
      router.push(redirectTo);
      router.refresh();
    } catch {
      setError("Ocorreu um erro inesperado. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendReset = async () => {
    setLoading(true);
    setError(null);
    try {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const { error } = await sendPasswordReset(email, `${origin}/auth/reset`);
      if (error) { setError(netErr(error)); return; }
      setStep("reset-sent");
    } catch {
      setError("Ocorreu um erro inesperado. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const backToEmail = () => { setStep("email"); setCode(""); setPassword(""); setError(null); };

  // Passo: codigo (OTP)
  if (step === "code") {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4"><Shield className="h-12 w-12 text-blue-500" /></div>
          <CardTitle className="text-2xl font-bold text-center">Digite o Código</CardTitle>
          <CardDescription className="text-center">
            Enviamos um código de 8 dígitos para <strong>{email}</strong>
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleVerifyCode}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code" className="text-center block">Código de Verificação</Label>
              <Input id="code" type="text" placeholder="12345678" value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                required disabled={loading} autoFocus className="text-center text-2xl tracking-widest" maxLength={8} />
            </div>
            {error && <div className="text-sm p-3 rounded-md bg-destructive/10 text-destructive">{error}</div>}
            <div className="text-xs text-muted-foreground text-center">
              Não recebeu? Verifique a pasta de spam ou aguarde alguns minutos. O código vale por 1 hora — use o e-mail mais recente.
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-2">
            <Button type="submit" className="w-full" disabled={loading || code.length !== 8}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Verificar Código
            </Button>
            <Button type="button" variant="ghost" className="w-full" onClick={backToEmail} disabled={loading}>Usar outro email</Button>
          </CardFooter>
        </form>
      </Card>
    );
  }

  // Passo: senha
  if (step === "password") {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4"><Lock className="h-12 w-12 text-blue-500" /></div>
          <CardTitle className="text-2xl font-bold text-center">Entrar com senha</CardTitle>
          <CardDescription className="text-center"><strong>{email}</strong></CardDescription>
        </CardHeader>
        <form onSubmit={handlePasswordLogin}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input id="password" type="password" placeholder="Sua senha" value={password}
                onChange={(e) => setPassword(e.target.value)} required disabled={loading} autoFocus />
            </div>
            {error && <div className="text-sm p-3 rounded-md bg-destructive/10 text-destructive">{error}</div>}
          </CardContent>
          <CardFooter className="flex flex-col space-y-2">
            <Button type="submit" className="w-full" disabled={loading || password.length < 1}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Entrar
            </Button>
            <Button type="button" variant="ghost" className="w-full" onClick={handleSendReset} disabled={loading}>
              Definir / recuperar senha
            </Button>
            <Button type="button" variant="ghost" className="w-full text-muted-foreground" onClick={backToEmail} disabled={loading}>
              Usar outro email
            </Button>
          </CardFooter>
        </form>
      </Card>
    );
  }

  // Passo: email de reset enviado
  if (step === "reset-sent") {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4"><MailCheck className="h-12 w-12 text-blue-500" /></div>
          <CardTitle className="text-2xl font-bold text-center">Verifique seu e-mail</CardTitle>
          <CardDescription className="text-center">
            Enviamos um link para <strong>{email}</strong> para você definir sua senha. Abra o e-mail e clique no link para continuar.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-xs text-muted-foreground text-center">
            O link vale por 1 hora. Não recebeu? Verifique a pasta de spam.
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-2">
          <Button type="button" variant="ghost" className="w-full" onClick={backToEmail} disabled={loading}>Voltar</Button>
        </CardFooter>
      </Card>
    );
  }

  // Passo: email (entrada)
  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <div className="flex justify-center mb-4"><Mail className="h-12 w-12 text-blue-500" /></div>
        <CardTitle className="text-2xl font-bold text-center">Entrar no Insight</CardTitle>
        <CardDescription className="text-center">Digite seu e-mail para continuar</CardDescription>
      </CardHeader>
      <form onSubmit={handleContinue}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" placeholder="seu@email.com" value={email}
              onChange={(e) => setEmail(e.target.value)} required disabled={loading} autoFocus />
          </div>
          {error && <div className="text-sm p-3 rounded-md bg-destructive/10 text-destructive">{error}</div>}
        </CardContent>
        <CardFooter>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Continuar
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
